import {
  Popover as PopoverRoot,
  PopoverProps as PrimitivePopoverProps,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives";
import React from "react";
import { cn } from "@gettactic/helpers";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";

export interface PopoverProps extends PrimitivePopoverProps {
  /** The body, mounted only while open. */
  content: React.ReactNode;
  /** A bar under the body, e.g. Clear / Apply. */
  footer?: React.ReactNode;
  /** A scrolling column beside the body, as tall as it. */
  sideContent?: React.ReactNode;
  className?: string;
  triggerClassname?: string;
  /** Props of the popover card, e.g. `aria-label`, `align`. */
  contentProps?: React.ComponentProps<typeof PopoverContent>;
  bodyProps?: React.ComponentProps<"div">;
  /** Uses the child as the trigger instead of wrapping it in a button. */
  triggerAsChild?: boolean;
  /** Extra attributes for the trigger button, e.g. `aria-describedby`. */
  triggerProps?: React.ComponentPropsWithoutRef<typeof PopoverTrigger>;
  mainContainerClassName?: string;
}

const classes = {
  Trigger: "w-full select-none",
  Content: "flex flex-1 border-1 border-neutral-100 rounded-brand p-0",
  Body: "p-4",
  Footer:
    "select-none flex items-center border-t-1 border-t-neutral-100 h-[44px]",
  SideContent:
    "flex flex-1 border-l-1 border-l-neutral-100 h-full bg-neutral py-6 pl-6 pr-5"
};

/**
 * A popover for filters and pickers: a full-width trigger, a padded body, an optional footer
 * bar and side column.
 */
export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  footer,
  sideContent,
  onOpenChange,
  contentProps,
  bodyProps,
  triggerAsChild = false,
  triggerProps,
  mainContainerClassName = "w-full",
  ...props
}) => {
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  const [mainContentHeight, setMainContentHeight] = React.useState(0);
  const [isOpen, setOpen] = React.useState(false);
  const hasSideContent = !!sideContent;

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    onOpenChange?.(open);
  };

  React.useEffect(() => {
    if (hasSideContent && mainContentRef.current && isOpen) {
      setMainContentHeight(mainContentRef.current.clientHeight);
    }
  }, [mainContentRef.current, isOpen, hasSideContent]);

  return (
    <PopoverRoot {...props} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        {...triggerProps}
        asChild={triggerAsChild}
        className={cn(classes.Trigger, props.triggerClassname)}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        showArrow={false}
        {...contentProps}
        className={cn(
          classes.Content,
          props.className,
          contentProps?.className
        )}
        style={{
          boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.1)",
          ...contentProps?.style
        }}
      >
        {(isOpen || props.open === true) && (
          <div className={"flex w-full items-stretch"}>
            <div ref={mainContentRef} className={mainContainerClassName}>
              <div
                {...bodyProps}
                className={cn(classes.Body, bodyProps?.className)}
              >
                {content}
              </div>
              {footer && <div className={classes.Footer}>{footer}</div>}
            </div>
            {hasSideContent && (
              <ScrollArea
                className={classes.SideContent}
                style={
                  mainContentHeight ? { height: mainContentHeight } : undefined
                }
              >
                {sideContent}
              </ScrollArea>
            )}
          </div>
        )}
      </PopoverContent>
    </PopoverRoot>
  );
};
